import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { collapseUnchanged, diffLines, diffStats } from "@/features/studio/diff"
import { cn } from "@/lib/utils"

/** Unified line diff of two texts with collapsed unchanged regions. */
export function PromptDiff({
  before,
  after,
  className,
}: {
  before: string
  after: string
  className?: string
}) {
  const { t } = useTranslation()
  const lines = useMemo(() => diffLines(before, after), [before, after])
  const stats = diffStats(lines)
  const rows = useMemo(() => collapseUnchanged(lines, 3), [lines])

  if (stats.added === 0 && stats.removed === 0)
    return <p className="text-muted-foreground text-sm">{t("studio.diff.identical")}</p>

  return (
    <div className={cn("overflow-hidden rounded-lg border", className)}>
      <div className="bg-muted/40 text-muted-foreground flex items-center gap-3 border-b px-3 py-1.5 text-xs tabular-nums">
        <span className="text-success font-medium">+{stats.added}</span>
        <span className="text-destructive font-medium">−{stats.removed}</span>
        <span>{t("studio.diff.lines")}</span>
      </div>
      <div className="max-h-[28rem] overflow-auto font-mono text-[0.78rem] leading-relaxed">
        <table className="w-full border-collapse">
          <tbody>
            {rows.map((r, i) =>
              r.op === "gap" ? (
                <tr key={`gap-${i}`} className="bg-muted/30 text-muted-foreground">
                  <td colSpan={3} className="px-3 py-0.5 text-center text-[0.7rem]">
                    {t("studio.diff.unchanged", { count: r.count })}
                  </td>
                </tr>
              ) : (
                <tr
                  key={i}
                  className={cn(r.op === "add" && "bg-success/10", r.op === "remove" && "bg-destructive/10")}
                >
                  <td className="text-muted-foreground w-10 border-r px-2 text-right select-none tabular-nums">
                    {r.oldLine ?? ""}
                  </td>
                  <td className="text-muted-foreground w-10 border-r px-2 text-right select-none tabular-nums">
                    {r.newLine ?? ""}
                  </td>
                  <td className="px-3 break-words whitespace-pre-wrap">
                    <span
                      aria-hidden
                      className={cn(
                        "mr-2 inline-block w-2 select-none",
                        r.op === "add" && "text-success",
                        r.op === "remove" && "text-destructive",
                      )}
                    >
                      {r.op === "add" ? "+" : r.op === "remove" ? "−" : " "}
                    </span>
                    <span className="sr-only">
                      {r.op === "add"
                        ? t("studio.diff.added")
                        : r.op === "remove"
                          ? t("studio.diff.removed")
                          : ""}
                    </span>
                    {r.text || " "}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
